/**
 * Featured Mines Component
 * Displays floating chips on the map for quick access to risk-scored sample mines.
 * Clicking a chip flies to the mine and opens its popup.
 */

import { mapService } from '../services/mapService.js';
import { filterManagerInstance } from '../services/filterManager.js';
import { useDataStore } from '../stores/dataStore.js';

export class FeaturedMines {
  constructor() {
    this.container = null;
    this.chips = [];
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.container = document.getElementById('featuredMines');
    if (!this.container) {
      console.warn('FeaturedMines: container not found');
      return;
    }

    this.chips = this.container.querySelectorAll('.featured-mine-chip');
    this.chips.forEach(chip => {
      chip.addEventListener('click', (e) => this.handleChipClick(e));
    });

    // Show chips only after welcome splash is dismissed (or immediately for returning visitors)
    document.addEventListener('welcomeSplashDismissed', () => this.show());

    // Also show when app is ready if splash was already dismissed previously
    document.addEventListener('applicationReady', () => {
      // Delay must exceed the 500ms splash show timeout in main.js
      // plus a frame for requestAnimationFrame to add .visible class
      setTimeout(() => {
        if (!this.container.classList.contains('visible')) {
          // Splash didn't show (returning visitor) — reveal chips now
          const overlay = document.getElementById('welcomeSplashOverlay');
          if (!overlay || overlay.style.display === 'none' || !overlay.classList.contains('visible')) {
            this.show();
          }
        }
      }, 1000);
    });

    console.log('FeaturedMines initialized');
  }

  show() {
    if (this.container) {
      this.container.classList.add('visible');
    }
  }

  async handleChipClick(e) {
    const chip = e.currentTarget;
    const mineId = parseInt(chip.dataset.mineId, 10);
    const lon = parseFloat(chip.dataset.lon);
    const lat = parseFloat(chip.dataset.lat);

    if (!mineId || isNaN(lon) || isNaN(lat)) return;

    // Visual feedback - briefly highlight the clicked chip
    chip.classList.add('active');
    setTimeout(() => chip.classList.remove('active'), 1200);

    // Fly to the mine
    mapService.flyTo([lon, lat], 8);

    // Set selection indicator immediately
    mapService.updateSelectionIndicator(mineId, [lon, lat]);

    // After fly animation, reload viewport data and show popup
    setTimeout(async () => {
      const viewport = mapService.getViewport();

      await filterManagerInstance.syncWithViewport(
        viewport.bounds,
        viewport.zoom,
        (data) => {
          const dataStore = useDataStore.getState();
          dataStore.setMines(data);

          // Try to highlight the mine from fresh data
          mapService.highlightMineById(mineId);
        },
        (error) => {
          console.error('FeaturedMines: failed to reload viewport data:', error);
        }
      );
    }, 1500);
  }

  destroy() {
    if (this.chips) {
      this.chips.forEach(chip => {
        chip.removeEventListener('click', this.handleChipClick);
      });
    }
  }
}

export const featuredMines = new FeaturedMines();
